import * as React from 'react';

import { Form } from '@independent-software/typeui/controls/Form';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Panel } from '@independent-software/typeui/controls/Panel';
import { DataTable } from '@independent-software/typeui/controls/DataTable';
import { Input } from '@independent-software/typeui/controls/Input';
import { Datum } from '@independent-software/typeui/formatters/Datum';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Number } from '@independent-software/typeui/formatters/Number';
import { Divider } from '@independent-software/typeui/controls/Divider';
import { Checkbox } from '@independent-software/typeui/controls/Checkbox';

import { Campaign, CampaignFactory, Project, ProjectFactory } from '../../resource/'; 
import { List, IListState, IListProps, Query, SearchFilter } from '../../services/';

import { IFilter } from '../../FilterContext';
import { IconBar, Export } from '../../modules';
import { Gate } from '../../controls/Gate';


interface IListCampaignsProps extends IListProps {
  filter: IFilter,
  /**
   * If set, list is prefiltered by a project and
   * project column and filter are hidden.
   */
  projectId?: number;  
  /**
   * Event is fired when a campaign is clicked.
   */
  onClick?: (campaign:Campaign) => void;
}

interface IListCampaignsState extends IListState<Campaign> {
  /**
   * Data for projects dropdown
   */
  projects: Project[];  
}

class ListCampaigns extends List<Campaign, IListCampaignsProps, IListCampaignsState> {
  constructor(props: IListCampaignsProps) {
    super(props, CampaignFactory, 'name', 'asc');

    // Initialize state (list initializes its own part of the state):
    this.state = {
      ...this.state,
      projects: []
    };

    // The project stored in the Query is an ID, not a Project.
    // Is the list prefiltered by a project? Then set a filter.
    if(this.props.projectId) {
      this.setFilter('project', 'eq', { id: this.props.projectId }, true);
    }      

    this.setFilter('system', 'in', this.props.filter.activeSystems.join(','));
  }

  componentDidMount() {
    this.handleProjectSearch();    
  }  
  
  handleSearch = (value:string) => {
    this.setFilter('q', 'like', value);
  }  

  handleChangeProject = (value: Project) => {
    this.setFilter('project', 'eq', value);
  }

  handleProjectSearch = (q?:string) => {
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    ProjectFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ projects: res.items }));
  }  

  handleChangeFromDate = (value:string) => {
    this.setFilter('date', 'gte', value);
  }  

  handleChangeToDate = (value:string) => {
    this.setFilter('date', 'lte', value);
  }     

  render() {
    let p = this.props;

    let filter = 
    <React.Fragment>
      <Panel.Content>
        <Form.Uncontrolled hint="Type to search records">
          <SearchFilter value={this.getFilter('q', 'like')} onSearch={this.handleSearch}/>
        </Form.Uncontrolled>
        {!p.projectId && 
          <Form.Uncontrolled hint="Filter by project">
            <Dropdown onSearch={this.handleProjectSearch} name="project" fluid clearable value={this.getFilter('project', 'eq')} data={this.state.projects} placeholder="Project" label={(item:Project) => item.name} onChange={this.handleChangeProject}>
              <Dropdown.Column weight={1}>{(item:Project) => item.name}</Dropdown.Column>
            </Dropdown>
          </Form.Uncontrolled>}        
        <Form.Uncontrolled hint="Filter by start date">
          <Input name="from" type="date" fluid clearable value={this.getFilter('date', 'gte')} placeholder="From date" onChange={this.handleChangeFromDate}/>
        </Form.Uncontrolled>
        <Form.Uncontrolled hint="Filter by end date">
          <Input name="to" type="date" fluid clearable value={this.getFilter('date', 'lte')} placeholder="To date" onChange={this.handleChangeToDate}/>
        </Form.Uncontrolled>            
      </Panel.Content>
      <Panel.Footer>
        <div style={{display: "flex", justifyContent: 'space-between'}}>
          <Gate right='can_delete_campaigns'>
            <Form.Uncontrolled>
              <Checkbox label="Include deleted items" name="trash" type="toggle" checked={this.getTrash()} onChange={this.handleTrash}/>
            </Form.Uncontrolled>
          </Gate>
          <Export onExport={this.handleExport}/>
        </div>
      </Panel.Footer>      
    </React.Fragment>

    return (
      <React.Fragment>
        <IconBar>
          <Panel.Icon icon="tools" width={350}>
            {filter}
          </Panel.Icon>
        </IconBar> 
        <Divider hidden/>
        <DataTable error={this.state.error} loading={this.state.loading} scrollTop={this.state.scrollTop} onScroll={this.handleScroll} data={this.state.items} onFetch={this.handleFetch} onClick={p.onClick} onOrder={this.handleOrder} order={this.getOrder()} dir={this.getDir()}>
          <DataTable.Column weight={1} label="Name" order="name" dir="asc">{(item:Campaign) => item.name}</DataTable.Column>
          <DataTable.Column weight={1} label="Date" order="date" dir="desc">{(item:Campaign) => <Datum.Distance value={item.date}/>}</DataTable.Column>
          <DataTable.Column weight={1} label="System" order="systems.name" dir="asc">{(item:Campaign) => item.system.name}</DataTable.Column>
          {!p.projectId && <DataTable.Column weight={1} label="Project" order="projects.name" dir="asc">{(item:Campaign) => item.project.name}</DataTable.Column>}
          <DataTable.Column align="right" weight={1} label="Samples" order="samples_count" dir="desc">{(item:Campaign) => <Number value={item.samples_count} decimals={0}/>}</DataTable.Column>
        </DataTable>
        <Dialog.Xhr open={this.state.exportError != null} error={this.state.exportError} onClose={this.handleCloseDialog}/>
      </React.Fragment>
    );
  }
}

export { ListCampaigns };
